import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import style from "./style.module.css";
import { EntCropProductionResponse, EntRegionData } from '../api/types';

interface MainTableProps {
    data?: EntCropProductionResponse | any;
    isLoading?: boolean;
}

interface RegionWithExpanded extends EntRegionData {
    expanded?: boolean;
}

export const MainTable: React.FC<MainTableProps> = ({ data, isLoading }) => {
    const { t } = useTranslation();
    const [regionsData, setRegionsData] = useState<RegionWithExpanded[]>([]);

    React.useEffect(() => {
        console.log("MainTable API data:", data);
        console.log("Data type:", typeof data);
        console.log("Data keys:", data ? Object.keys(data) : "no data");

        if (!data) {
            setRegionsData([]);
            return;
        }

        // Проверяем различные возможные структуры данных
        let regions: any[] = [];

        if (data?.data && Array.isArray(data.data)) {
            console.log("Processing data.data:", data.data.length, "items");
            regions = data.data;
        } else if (Array.isArray(data)) {
            console.log("Data is array directly:", data.length, "items");
            regions = data;
        } else if (data?.res && data.res.data && Array.isArray(data.res.data)) {
            console.log("Processing data.res.data:", data.res.data.length, "items");
            regions = data.res.data;
        } else if (data?.result && Array.isArray(data.result)) {
            console.log("Processing data.result:", data.result.length, "items");
            regions = data.result;
        } else {
            console.log("Unknown data structure:", data);
            setRegionsData([]);
            return;
        }

        const initialRegions = regions.map(region => ({
            ...region,
            expanded: false,
        }));
        setRegionsData(initialRegions);
        console.log("Set regionsData:", initialRegions.length, "items");
    }, [data]);


    const totalAmount = data?.totalAmount || data?.res?.totalAmount || regionsData.reduce((sum, region) => sum + (region.totalAmount || 0), 0);
    const totalCleaningArea = data?.totalCleaningArea || data?.res?.totalCleaningArea || regionsData.reduce((sum, region) => sum + (region.totalCleaningArea || 0), 0);
    const totalProductivity = data?.totalProductivity || data?.res?.totalProductivity || 0;


    const toggleRegion = (index: number) => {
        const updatedRegions = [...regionsData];
        updatedRegions[index].expanded = !updatedRegions[index].expanded;
        setRegionsData(updatedRegions);
    };

    return (
        <div className={style.tableContainer}>
            <div className={style.tableHeader}>
                <h3 className={style.tableTitle}>{t('Данные по республике')}</h3>
                <div className={style.summaryInfo}>
                    <div className={style.summaryItem}>
                        <span className={style.summaryLabel}>Общий объем:</span>
                        <span className={style.summaryValue}>{totalAmount.toLocaleString()} тонн</span>
                    </div>
                    <div className={style.summaryItem}>
                        <span className={style.summaryLabel}>Общая площадь уборки:</span>
                        <span className={style.summaryValue}>{totalCleaningArea.toLocaleString()} га</span>
                    </div>
                    <div className={style.summaryItem}>
                        <span className={style.summaryLabel}>Общая продуктивность:</span>
                        <span className={style.summaryValue}>{totalProductivity.toFixed(2)} ц/га</span>
                    </div>
                </div>
            </div>

            <table className={style.mainTable}>
                <thead>
                    <tr>
                        <th className={style.areaColumn}>{t('Регион/Категория/Продукт')}</th>
                        <th className={style.countColumn}>{t('Объем (тонн)')}</th>
                        <th className={style.countColumn}>{t('Площадь (га)')}</th>
                        <th className={style.countColumn}>{t('Прод-ть (ц/га)')}</th>
                    </tr>
                </thead>
                <tbody>
                    {isLoading ? (
                        <tr>
                            <td colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>
                                {t('Загрузка...')}
                            </td>
                        </tr>
                    ) : regionsData && regionsData.length > 0 ? (
                        regionsData.map((region, index) => (
                            <React.Fragment key={`region-group-${index}`}>
                                <tr className={style.regionRow}>
                                    <td className={style.regionCell}>
                                        <span
                                            className={style.expandButton}
                                            onClick={() => toggleRegion(index)}
                                        >
                                            {region.expanded ? '−' : '+'}
                                        </span>
                                        {region.nameRu || region.nameKy}
                                    </td>
                                    <td className={style.countCell}>
                                        <div>
                                            {region.totalAmount.toLocaleString()}
                                        </div>
                                        {region.amountPercentage !== undefined && (
                                            <div className={style.percentageText}>
                                                ({region.amountPercentage.toFixed(1)}%)
                                            </div>
                                        )}
                                    </td>
                                    <td className={style.countCell}>
                                        <div>
                                            {region.totalCleaningArea?.toLocaleString() || 0}
                                        </div>
                                        {region.totalCleaningAreaPercentage !== undefined && (
                                            <div className={style.percentageText}>
                                                ({region.totalCleaningAreaPercentage.toFixed(1)}%)
                                            </div>
                                        )}
                                    </td>
                                    <td className={style.countCell}>
                                        <div>
                                            {region.totalProductivity?.toFixed(2) || 0}
                                        </div>
                                        {region.totalProductivityPercentage !== undefined && (
                                            <div className={style.percentageText}>
                                                ({region.totalProductivityPercentage.toFixed(1)}%)
                                            </div>
                                        )}
                                    </td>
                                </tr>
                                {region.expanded && region.categories?.map((category, subIndex) => (
                                    <tr key={`subitem-${index}-${subIndex}`} className={style.subItemRow}>
                                        <td className={style.subItemCell}>{category.nameRu || category.nameKy}</td>
                                        <td className={style.countCell}>{category.totalAmount.toLocaleString()}</td>
                                        <td className={style.countCell}>{category.totalCleaningArea?.toLocaleString() || 0}</td>
                                        <td className={style.countCell}>{category.totalProductivity?.toFixed(2) || 0}</td>
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>
                                {t('Данных нет')}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}